import React, { Fragment, useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import { Modal } from "react-bootstrap"
import moment from "moment"
import { FaChevronLeft } from "react-icons/fa"

import { request } from "../../services/request"
import {
  SubscriptionStateData,
  SubscriptionStateResponse,
  SubscriptionTier,
} from "../../services/authinterface"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import SecondaryButton from "../../components/subComponents/SecondaryButton"
import CustomPricingWizard from "../../components/common/CustomPricingWizard"
import { useDispatch } from "react-redux"
import { stripeDashboardUrl, formatCents } from "../../utils/utils"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import "../../css/billing.css"

const Icon: any = FaChevronLeft

const PRODUCT_TYPES: ("SOS" | "Sensor")[] = ["SOS", "Sensor"]

// Everything an admin can do here that writes. Each carries the sentence shown in the
// confirmation modal, because all of these either grant access, move money, or cancel a
// subscription with no refund, and none of that should happen on a stray click.
type ActionKey = "sync" | "reprovision" | "confirmQuantity" | "resolveDispute" | "exemption"

const StatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status) return <span className='billing-badge is-neutral'>No subscription</span>
  const good = status === "active" || status === "trialing"
  return (
    <span className={`billing-badge ${good ? "is-good" : "is-bad"}`}>
      {status.replace(/_/g, " ")}
    </span>
  )
}

const Banner: React.FC<{ tone: "warn" | "error" | "info"; children: React.ReactNode }> = ({
  tone,
  children,
}) => <div className={`billing-banner tone-${tone}`}>{children}</div>

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className='billing-stat'>
    <span className='billing-stat-label'>{label}</span>
    <span className='billing-stat-value'>{value}</span>
  </div>
)

const TierTable: React.FC<{
  tiers: SubscriptionTier[]
  allocation: { priceId: string; seats: number }[]
}> = ({ tiers, allocation }) => {
  if (tiers.length === 0) {
    return <div className='billing-empty'>No prices are attached to this subscription.</div>
  }

  const seatsFor = (priceId: string) =>
    allocation.find((a) => a.priceId === priceId)?.seats ?? 0

  return (
    <div className='billing-table-wrap'>
      <table className='billing-table'>
        <thead>
          <tr>
            <th>Price</th>
            <th>Rate / seat / yr</th>
            <th>Seat cap</th>
            <th>Expires</th>
            <th>Billed now</th>
            <th>Would hold today</th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((tier) => {
            // A frozen tier means the backend could not read this Price's gs_* metadata
            // and is ignoring it. Its `problem` is written for an admin to act on, so it
            // is shown word for word rather than summarised.
            if (!tier.usable) {
              return (
                <tr key={tier.priceId} className='is-frozen'>
                  <td>
                    <span className='billing-tier-name'>Ignored price</span>
                    <span className='billing-price-id'>{tier.priceId}</span>
                  </td>
                  <td colSpan={5}>
                    <strong>This price is being ignored.</strong> {tier.problem}
                  </td>
                </tr>
              )
            }
            return (
              <tr key={tier.priceId}>
                <td>
                  <span className='billing-tier-name'>
                    {tier.label || "Untitled price"}
                    {tier.isStandard && (
                      <span className='billing-badge is-standard'>standard</span>
                    )}
                  </span>
                  <span className='billing-price-id'>{tier.priceId}</span>
                </td>
                <td>{formatCents(tier.unitAmount)}</td>
                <td>{tier.seatCap === "unlimited" ? "Unlimited" : tier.seatCap}</td>
                <td>{tier.expiresAt === "never" ? "Never" : tier.expiresAt}</td>
                <td>{tier.quantity}</td>
                <td>{seatsFor(tier.priceId)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Sales-tax exemption. Deliberately its own modal rather than a button on the owner
// panel, because unlike the other actions here it needs real input: Stripe does not
// validate exemption certificates, so we have to record the reference and hold the
// document ourselves to be able to produce it on audit.
//
// The banner is the important part of this component. "Exempt" and "comped" get confused
// constantly, and the consequences are very different: this zeroes the tax on a bill the
// customer still pays, while the comp button on the owner panel cancels their
// subscription outright with no refund.
const TaxExemptionModal: React.FC<{
  show: boolean
  onClose: () => void
  owner: SubscriptionStateData["owner"]
  onSubmit: (payload: {
    taxExempt: "none" | "exempt" | "reverse"
    certificateRef: string
    expiresAt: string
    certificate: File | null
  }) => Promise<void>
  isSubmitting: boolean
}> = ({ show, onClose, owner, onSubmit, isSubmitting }) => {
  const current = owner.taxExemption
  const [status, setStatus] = useState<"none" | "exempt" | "reverse">(current.status)
  const [certificateRef, setCertificateRef] = useState(current.certificateRef ?? "")
  const [expiresAt, setExpiresAt] = useState(current.expiresAt ?? "")
  const [certificate, setCertificate] = useState<File | null>(null)

  // Reset whenever the modal reopens, so a cancelled edit does not leave stale values
  // behind for the next one.
  useEffect(() => {
    if (!show) return
    setStatus(current.status)
    setCertificateRef(current.certificateRef ?? "")
    setExpiresAt(current.expiresAt ?? "")
    setCertificate(null)
  }, [show, current.status, current.certificateRef, current.expiresAt])

  const needsCertificate = status === "exempt"
  const canSubmit = !needsCertificate || certificateRef.trim().length > 0

  return (
    <Modal className='comman-modal' show={show} onHide={onClose} centered>
      <Modal.Header closeButton className='comman-modal-header'>
        <Modal.Title className='comman-modal-title'>Sales tax exemption</Modal.Title>
      </Modal.Header>
      <Modal.Body className='comman-modal-body'>
        <Banner tone='info'>
          <strong>This only affects sales tax.</strong> An exempt customer still pays for their
          seats at the usual price, and their subscription keeps running. To stop billing them
          entirely, use <em>Exempt from all billing</em> on the owner panel instead.
        </Banner>

        <div className='mb-3'>
          <label className='billing-stat-label' htmlFor='taxExemptStatus'>
            Status
          </label>
          <select
            id='taxExemptStatus'
            className='form-control'
            value={status}
            onChange={(e) => setStatus(e.target.value as "none" | "exempt" | "reverse")}
          >
            <option value='none'>None - charge sales tax normally</option>
            <option value='exempt'>Exempt - do not charge sales tax</option>
            <option value='reverse'>Reverse charge - customer accounts for the tax</option>
          </select>
        </div>

        {needsCertificate && (
          <Fragment>
            <div className='mb-3'>
              <label className='billing-stat-label' htmlFor='taxExemptRef'>
                Certificate reference <span className='text-danger'>*</span>
              </label>
              <input
                id='taxExemptRef'
                className='form-control'
                type='text'
                value={certificateRef}
                placeholder='e.g. FL-DR14-123456'
                onChange={(e) => setCertificateRef(e.target.value)}
              />
              <small className='text-muted'>
                Required. Stripe does not validate exemption certificates, so this is the only
                record tying the exemption to real paperwork.
              </small>
            </div>

            <div className='mb-3'>
              <label className='billing-stat-label' htmlFor='taxExemptExpiry'>
                Expires
              </label>
              <input
                id='taxExemptExpiry'
                className='form-control'
                type='date'
                value={expiresAt ? String(expiresAt).slice(0, 10) : ""}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <small className='text-muted'>
                Optional. When set, the exemption reverts automatically on this date and an alert
                is raised so the certificate can be renewed.
              </small>
            </div>

            <div className='mb-3'>
              <label className='billing-stat-label' htmlFor='taxExemptFile'>
                Certificate document (PDF or image)
              </label>
              <input
                id='taxExemptFile'
                className='form-control'
                type='file'
                accept='application/pdf,image/*'
                onChange={(e) => setCertificate(e.target.files?.[0] ?? null)}
              />
              <small className='text-muted'>
                {current.hasCertificateOnFile
                  ? "A document is already on file. Choosing a new one replaces it; leaving this empty keeps it. Download it from the owner panel to check which one is stored."
                  : "Upload the signed certificate so it can be produced if this sale is ever audited."}
              </small>
            </div>
          </Fragment>
        )}

        {status === "none" && current.status !== "none" && (
          <Banner tone='warn'>
            Their next invoice will include sales tax. The certificate reference and the stored
            document are kept on file: they are the evidence for the invoices already issued
            while the exemption was active, and an audit can come years later.
          </Banner>
        )}

        <div className='d-flex gap-3 justify-content-center mt-3'>
          <SecondaryButton
            type='button'
            label='Cancel'
            className='editFormButton'
            onClick={onClose}
            disabled={isSubmitting}
          />
          <PrimaryButton
            type='button'
            label={isSubmitting ? "Saving..." : "Save"}
            className='editFormButton'
            onClick={() => onSubmit({ taxExempt: status, certificateRef, expiresAt, certificate })}
            disabled={isSubmitting || !canSubmit}
          />
        </div>
      </Modal.Body>
    </Modal>
  )
}

// Billing address entry for sales. This is the surface that supports contract and PO
// customers: the address is already on the paperwork, so entering it here means the
// customer is never prompted for one. Self-serve customers supply theirs through the
// setup-mode Checkout Session during onboarding instead.
//
// All fields are required together because Stripe Tax cannot calculate US sales tax from
// a partial address - country alone, or country plus state, resolves no jurisdiction at
// all - and a half-filled address is worse than none, since it looks collected.
const BillingAddressModal: React.FC<{
  show: boolean
  onClose: () => void
  owner: SubscriptionStateData["owner"]
  onSubmit: (payload: {
    billingAddressLine1: string
    billingCity: string
    billingState: string
    billingPostalCode: string
    billingCountry: string
    organizationName: string
  }) => Promise<void>
  isSubmitting: boolean
}> = ({ show, onClose, owner, onSubmit, isSubmitting }) => {
  const existing = owner.billingAddress
  const [line1, setLine1] = useState(existing.line1 ?? "")
  const [city, setCity] = useState(existing.city ?? "")
  const [state, setState] = useState(existing.state ?? "")
  const [postalCode, setPostalCode] = useState(existing.postalCode ?? "")
  const [country, setCountry] = useState(existing.country ?? "US")
  const [organizationName, setOrganizationName] = useState(owner.organizationName ?? "")

  useEffect(() => {
    if (!show) return
    setLine1(existing.line1 ?? "")
    setCity(existing.city ?? "")
    setState(existing.state ?? "")
    setPostalCode(existing.postalCode ?? "")
    setCountry(existing.country ?? "US")
    setOrganizationName(owner.organizationName ?? "")
  }, [
    show,
    existing.line1,
    existing.city,
    existing.state,
    existing.postalCode,
    existing.country,
    owner.organizationName,
  ])

  const canSubmit =
    line1.trim() !== "" &&
    city.trim() !== "" &&
    state.trim() !== "" &&
    postalCode.trim() !== "" &&
    country.trim().length === 2

  return (
    <Modal className='comman-modal' show={show} onHide={onClose} centered>
      <Modal.Header closeButton className='comman-modal-header'>
        <Modal.Title className='comman-modal-title'>Billing address</Modal.Title>
      </Modal.Header>
      <Modal.Body className='comman-modal-body'>
        <Banner tone='info'>
          Stripe Tax needs a full address to work out which sales tax applies. A street address is
          worth getting right: in many US states the rate varies within a single ZIP code.
        </Banner>

        <div className='mb-3'>
          <label className='billing-stat-label' htmlFor='orgName'>
            Organization name
          </label>
          <input
            id='orgName'
            className='form-control'
            type='text'
            value={organizationName}
            placeholder='e.g. Springfield School District'
            onChange={(e) => setOrganizationName(e.target.value)}
          />
          <small className='text-muted'>
            Optional, but this is what appears on their invoices and on any exemption
            certificate. Left blank, invoices use the contact's own name.
          </small>
        </div>

        <div className='mb-3'>
          <label className='billing-stat-label' htmlFor='addrLine1'>
            Street address <span className='text-danger'>*</span>
          </label>
          <input
            id='addrLine1'
            className='form-control'
            type='text'
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
          />
        </div>

        <div className='row'>
          <div className='col-6 mb-3'>
            <label className='billing-stat-label' htmlFor='addrCity'>
              City <span className='text-danger'>*</span>
            </label>
            <input
              id='addrCity'
              className='form-control'
              type='text'
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
          <div className='col-6 mb-3'>
            <label className='billing-stat-label' htmlFor='addrState'>
              State <span className='text-danger'>*</span>
            </label>
            <input
              id='addrState'
              className='form-control'
              type='text'
              value={state}
              onChange={(e) => setState(e.target.value)}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-6 mb-3'>
            <label className='billing-stat-label' htmlFor='addrPostal'>
              ZIP / postal code <span className='text-danger'>*</span>
            </label>
            <input
              id='addrPostal'
              className='form-control'
              type='text'
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
            />
          </div>
          <div className='col-6 mb-3'>
            <label className='billing-stat-label' htmlFor='addrCountry'>
              Country <span className='text-danger'>*</span>
            </label>
            <input
              id='addrCountry'
              className='form-control'
              type='text'
              maxLength={2}
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
            />
            <small className='text-muted'>Two-letter code, e.g. US.</small>
          </div>
        </div>

        <div className='d-flex gap-3 justify-content-center mt-3'>
          <SecondaryButton
            type='button'
            label='Cancel'
            className='editFormButton'
            onClick={onClose}
            disabled={isSubmitting}
          />
          <PrimaryButton
            type='button'
            label={isSubmitting ? "Saving..." : "Save"}
            className='editFormButton'
            onClick={() =>
              onSubmit({
                billingAddressLine1: line1,
                billingCity: city,
                billingState: state,
                billingPostalCode: postalCode,
                billingCountry: country,
                organizationName,
              })
            }
            disabled={isSubmitting || !canSubmit}
          />
        </div>
      </Modal.Body>
    </Modal>
  )
}

const CustomerBilling: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { ownerId } = useParams()

  const [state, setState] = useState<Record<string, SubscriptionStateData | null>>({
    SOS: null,
    Sensor: null,
  })
  const [pending, setPending] = useState<{
    action: ActionKey
    type: "SOS" | "Sensor"
    title: string
    body: React.ReactNode
    // Exemption is account-wide, not per-type, so it is captured here at the moment the
    // button is clicked (from the same combined owner the button's own label uses)
    // rather than re-derived from state[type] in runAction - state.SOS and state.Sensor
    // are two independent fetches, and re-deriving from one of them by a hardcoded type
    // can disagree with the value the admin actually saw on the button.
    currentIsDirect?: boolean
  } | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [wizardType, setWizardType] = useState<"SOS" | "Sensor" | null>(null)
  // Both of these take real input, so they are their own forms rather than entries in the
  // confirm-modal flow above (which only ever needs a yes/no).
  const [showTaxExemption, setShowTaxExemption] = useState(false)
  const [showAddress, setShowAddress] = useState(false)

  const fetchState = useCallback(async () => {
    if (!ownerId) return
    dispatch(changeloaderState(true))
    try {
      // Each product line settles independently. Fetching both with a plain Promise.all
      // meant one type's request rejecting failed the whole call, so a healthy Sensor
      // subscription reported "could not load" too whenever the SOS lookup alone errored
      // - even though renderSection already has fallback UI built for exactly one type
      // failing.
      const results = await Promise.all(
        PRODUCT_TYPES.map((type) =>
          request<SubscriptionStateResponse>({
            url: `/adminSubscriptions/${ownerId}/${type}`,
            method: "GET",
          })
            .then((res) => [type, res.data ?? null] as const)
            .catch((error: any) => {
              toast.error(`${type}: ${error.message}`)
              return [type, null] as const
            }),
        ),
      )
      setState(Object.fromEntries(results))
    } finally {
      dispatch(changeloaderState(false))
    }
  }, [dispatch, ownerId])

  useEffect(() => {
    fetchState()
  }, [fetchState])

  const runAction = async () => {
    if (!pending || !ownerId) return
    setIsSubmitting(true)
    try {
      if (pending.action === "exemption") {
        await request({
          url: `/adminSubscriptions/direct-exemption`,
          method: "POST",
          data: { ownerId, isDirect: !pending.currentIsDirect },
        })
      } else {
        const flags: Record<string, boolean> = {}
        if (pending.action === "reprovision") flags.reprovision = true
        if (pending.action === "confirmQuantity") flags.confirmLocalQuantity = true
        if (pending.action === "resolveDispute") flags.resolveDispute = true

        await request({
          url: `/adminSubscriptions/sync`,
          method: "POST",
          data: { ownerId, type: pending.type, ...flags },
        })
      }
      toast.success("Done")
      setPending(null)
      await fetchState()
    } catch (error: any) {
      // The API explains refusals in prose (a quantity that does not match, a metadata
      // problem, two candidate subscriptions). Show it verbatim.
      toast.error(`${error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const submitTaxExemption = async (payload: {
    taxExempt: "none" | "exempt" | "reverse"
    certificateRef: string
    expiresAt: string
    certificate: File | null
  }) => {
    if (!ownerId) return
    setIsSubmitting(true)
    try {
      // multipart because the certificate document rides along with the fields. The
      // endpoint accepts either shape, but sending one request keeps the document and the
      // status it belongs to from ever disagreeing.
      const form = new FormData()
      form.append("ownerId", ownerId)
      form.append("taxExempt", payload.taxExempt)
      if (payload.certificateRef.trim()) {
        form.append("certificateRef", payload.certificateRef.trim())
      }
      if (payload.expiresAt) form.append("expiresAt", payload.expiresAt)
      if (payload.certificate) form.append("certificate", payload.certificate)

      await request({
        url: `/adminSubscriptions/tax-exemption`,
        method: "POST",
        data: form,
      })
      toast.success("Tax exemption updated")
      setShowTaxExemption(false)
      await fetchState()
    } catch (error: any) {
      // The API refuses in prose - a missing certificate reference, an owner with no
      // Stripe customer yet, or Stripe rejecting the change outright. Show it verbatim.
      toast.error(`${error?.response?.data?.message || error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // The certificate is served by an admin-authenticated endpoint, so it cannot simply be
  // an href - the bearer token has to go with the request. Fetched as a blob and handed to
  // the browser as a download instead.
  const downloadCertificate = async () => {
    if (!ownerId) return
    try {
      const blob = await request<Blob>({
        url: `/adminSubscriptions/tax-exemption/certificate/${ownerId}`,
        method: "GET",
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `tax-exemption-certificate-${ownerId}`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      // A 410 means the row says there is a document but the file is gone from disk, which
      // is worth seeing verbatim rather than as a generic failure.
      toast.error(
        error?.response?.data?.message ||
          error.message ||
          "Could not download the certificate",
      )
    }
  }

  const submitAddress = async (payload: Record<string, string>) => {
    if (!ownerId) return
    setIsSubmitting(true)
    try {
      await request({
        url: `/adminCustomers/updateBillingAddress/${ownerId}`,
        method: "POST",
        data: payload,
      })
      toast.success("Billing address updated")
      setShowAddress(false)
      await fetchState()
    } catch (error: any) {
      // A 400 here usually means Stripe could not resolve the address to a tax location.
      // The address is saved either way, so the modal stays open for a correction rather
      // than closing and looking like it worked.
      toast.error(`${error?.response?.data?.message || error.message}`)
      await fetchState()
    } finally {
      setIsSubmitting(false)
    }
  }

  // The wizard offers both: a plain sync just re-reads and re-parses tiers (safe, no
  // billing effect), while reprovision: true also re-runs seat allocation immediately,
  // which can charge a prorated amount right away. Left to the admin to choose, with the
  // wizard explaining the difference rather than this page picking one for them.
  const verifyFromWizard = async (reprovision: boolean) => {
    if (!ownerId || !wizardType) return
    try {
      await request({
        url: `/adminSubscriptions/sync`,
        method: "POST",
        data: { ownerId, type: wizardType, ...(reprovision ? { reprovision: true } : {}) },
      })
      toast.success(
        reprovision
          ? "Synced and applied. Check the tier table for any problems."
          : "Synced. Check the tier table for any problems.",
      )
      setWizardType(null)
      await fetchState()
    } catch (error: any) {
      toast.error(`${error.message}`)
    }
  }

  const renderSection = (type: "SOS" | "Sensor") => {
    const data = state[type]
    if (!data) {
      return (
        <div className='billing-panel'>
          <div className='billing-panel-header'>
            <div className='billing-panel-heading'>
              <h3>{type}</h3>
            </div>
          </div>
          <div className='billing-empty'>Could not load billing state for this product line.</div>
        </div>
      )
    }

    const sub = data.subscription
    const disputed = sub?.disputeSuspendedAt

    return (
      <div className='billing-panel'>
        <div className='billing-panel-header'>
          <div className='billing-panel-heading'>
            <h3>{type}</h3>
            <StatusBadge status={data.stripeStatus ?? sub?.status ?? null} />
          </div>
          <div className='billing-actions'>
            <PrimaryButton
              label='Set up custom pricing'
              onClick={() => setWizardType(type)}
            />
          </div>
        </div>

        {disputed && (
          <Banner tone='error'>
            <strong>Payment disputed on {moment(disputed).format("MMM-DD-YYYY")}.</strong> Access is
            suspended. This never clears on its own: settle the dispute in Stripe first, then use
            "Resolve dispute" below to restore access.
          </Banner>
        )}

        {data.problem && <Banner tone='warn'>{data.problem}</Banner>}

        {data.candidates.length > 0 && (
          <Banner tone='warn'>
            More than one subscription matches: {data.candidates.join(", ")}. Cancel the one that
            is not wanted in Stripe, then sync again.
          </Banner>
        )}

        {data.unallocated > 0 && (
          <Banner tone='warn'>
            <strong>
              {data.unallocated} seat{data.unallocated === 1 ? "" : "s"} cannot be priced.
            </strong>{" "}
            Every tier is capped and none absorbs the overflow, so nothing is being billed for them
            and no change is being made in Stripe. Raise a seat cap, or attach the standard annual
            price as an overflow tier.
          </Banner>
        )}

        {data.graceWindow?.active && (
          <Banner tone='info'>
            <strong>Seat count not yet confirmed.</strong> Until{" "}
            {moment(data.graceWindow.endsAt).format("MMM-DD-YYYY")}, a local device count below
            Stripe's quantity is left alone rather than shrinking or cancelling the subscription.
            Once the customer has finished installing, use "Confirm seat count".
          </Banner>
        )}

        {sub && sub.quantity !== data.localDeviceCount && (
          <Banner tone='warn'>
            Stripe is billing {sub.quantity} seat{sub.quantity === 1 ? "" : "s"} but{" "}
            {data.localDeviceCount} device{data.localDeviceCount === 1 ? " is" : "s are"} on record.
            {sub.quantity > data.localDeviceCount
              ? " Seats already paid for are never refunded, so this only affects future billing."
              : " Adding the missing devices will charge a prorated amount immediately."}
          </Banner>
        )}

        {data.owner.paymentUpdated !== 1 && !data.owner.is_direct && (
          <Banner tone='warn'>
            <strong>No payment method on file.</strong> This customer cannot pair an SOS button or
            install a sensor until they add one.
          </Banner>
        )}

        <div className='billing-stats'>
          <Stat label='Devices on record' value={data.localDeviceCount} />
          <Stat label='Billed quantity' value={sub ? sub.quantity : "-"} />
          <Stat label='Paid for this period' value={sub ? sub.periodPaidQuantity : "-"} />
          <Stat
            label='Payment method'
            value={data.owner.paymentUpdated === 1 ? "On file" : "None on file"}
          />
        </div>

        <TierTable tiers={data.tiers} allocation={data.allocation} />

        <div className='billing-actions'>
          <PrimaryButton
            label='Sync from Stripe'
            onClick={() =>
              setPending({
                action: "sync",
                type,
                title: `Sync ${type} from Stripe?`,
                body: (
                  <p>
                    Reads this customer's subscription from Stripe and adopts it. If it is active,
                    that grants them access. Nothing is charged.
                  </p>
                ),
              })
            }
          />
          <PrimaryButton
            label='Apply Stripe changes now'
            onClick={() =>
              setPending({
                action: "reprovision",
                type,
                title: `Apply ${type} changes now?`,
                body: (
                  <p>
                    Re-runs seat allocation immediately instead of waiting for the nightly run.{" "}
                    <strong>
                      If this adds seats, the customer is charged a prorated amount straight away.
                    </strong>{" "}
                    Removing seats never produces a refund or credit.
                  </p>
                ),
              })
            }
          />
          <PrimaryButton
            label='Confirm seat count'
            disabled={!data.graceWindow?.active}
            onClick={() =>
              setPending({
                action: "confirmQuantity",
                type,
                title: `Confirm the ${type} seat count?`,
                body: (
                  <p>
                    Only do this once the customer has actually finished installing. It lifts the
                    safety window, so from then on{" "}
                    <strong>
                      a device count below the billed quantity will reduce or cancel the
                      subscription with no refund.
                    </strong>{" "}
                    Stripe currently bills {sub?.quantity ?? "-"} and {data.localDeviceCount} device
                    {data.localDeviceCount === 1 ? " is" : "s are"} on record; these must match.
                  </p>
                ),
              })
            }
          />
          {disputed && (
            <PrimaryButton
              label='Resolve dispute'
              onClick={() =>
                setPending({
                  action: "resolveDispute",
                  type,
                  title: "Clear the dispute suspension?",
                  body: (
                    <p>
                      This restores the customer's access. Settle the dispute in Stripe first: this
                      only clears the suspension on our side, it does not respond to Stripe on your
                      behalf.
                    </p>
                  ),
                })
              }
            />
          )}
        </div>
      </div>
    )
  }

  const owner = state.SOS?.owner ?? state.Sensor?.owner ?? null
  const address = owner?.billingAddress
  const stripeMode = state.SOS?.stripeMode ?? state.Sensor?.stripeMode
  const stripeProductId = state.SOS?.stripeProductId ?? state.Sensor?.stripeProductId

  return (
    <section className='dashboard-section'>
      <ToastContainer position='top-right' autoClose={2000} theme='light' />

      <Modal
        className='comman-modal'
        show={pending !== null}
        onHide={() => setPending(null)}
        centered
      >
        <Modal.Header closeButton className='comman-modal-header'>
          <Modal.Title className='comman-modal-title'>{pending?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className='comman-modal-body'>
          {pending?.body}
          <div className='d-flex gap-3 justify-content-center mt-3'>
            <SecondaryButton
              type='button'
              label='Cancel'
              className='editFormButton'
              onClick={() => setPending(null)}
              disabled={isSubmitting}
            />
            <PrimaryButton
              type='button'
              label={isSubmitting ? "Working..." : "Confirm"}
              className='editFormButton'
              onClick={runAction}
              disabled={isSubmitting}
            />
          </div>
        </Modal.Body>
      </Modal>

      {owner && (
        <TaxExemptionModal
          show={showTaxExemption}
          onClose={() => setShowTaxExemption(false)}
          owner={owner}
          onSubmit={submitTaxExemption}
          isSubmitting={isSubmitting}
        />
      )}

      {owner && (
        <BillingAddressModal
          show={showAddress}
          onClose={() => setShowAddress(false)}
          owner={owner}
          onSubmit={submitAddress}
          isSubmitting={isSubmitting}
        />
      )}

      {wizardType && (
        <CustomPricingWizard
          show={wizardType !== null}
          onClose={() => setWizardType(null)}
          productType={wizardType}
          stripeMode={stripeMode}
          stripeProductId={stripeProductId}
          onVerify={verifyFromWizard}
        />
      )}

      <div className='container'>
        <div className='row'>
          <div className='col-12 mb-3'>
            <div className='d-flex gap-1 customer-detail-line'>
              <button onClick={() => navigate(-1)} className='d-flex align-items-center'>
                <Icon size={20} />
              </button>
              <div className='dashboard-table-title-section'>
                <h2>Billing</h2>
              </div>
            </div>
          </div>

          {owner && (
            <div className='col-12'>
              <div className='billing-panel'>
                <h3 className='billing-owner-name'>
                  {[owner.first_name, owner.last_name].filter(Boolean).join(" ") || owner.email}
                </h3>
                <p className='billing-subtitle'>{owner.email}</p>

                {owner.is_direct && (
                  <Banner tone='info'>
                    <strong>This customer is comped.</strong> They are permanently exempt from all
                    SOS and Sensor billing, and no subscription will be created or kept for them
                    while that is set.
                  </Banner>
                )}

                <div className='billing-owner-meta'>
                  <div className='billing-stat'>
                    <span className='billing-stat-label'>
                      Stripe customer{stripeMode ? ` (${stripeMode} mode)` : ""}
                    </span>
                    <span className='billing-stat-value'>
                      {owner.stripeCustomerId ? (
                        <a
                          href={stripeDashboardUrl(stripeMode, `customers/${owner.stripeCustomerId}`)}
                          target='_blank'
                          rel='noreferrer'
                        >
                          {owner.stripeCustomerId}
                        </a>
                      ) : (
                        "None yet"
                      )}
                    </span>
                  </div>
                  <div className='billing-stat'>
                    <span className='billing-stat-label'>Billing address</span>
                    <span className='billing-stat-value'>
                      {address?.line1
                        ? [
                            address.line1,
                            address.city,
                            address.state,
                            address.postalCode,
                            address.country,
                          ]
                            .filter(Boolean)
                            .join(", ")
                        : "Not collected"}
                    </span>
                  </div>
                  <div className='billing-stat'>
                    <span className='billing-stat-label'>Organization</span>
                    <span className='billing-stat-value'>
                      {owner.organizationName || "Not set"}
                    </span>
                  </div>
                  <div className='billing-stat'>
                    <span className='billing-stat-label'>Sales tax</span>
                    <span className='billing-stat-value'>
                      {owner.taxExemption.status === "none"
                        ? "Charged normally"
                        : owner.taxExemption.status === "exempt"
                          ? `Exempt${
                              owner.taxExemption.certificateRef
                                ? ` (${owner.taxExemption.certificateRef})`
                                : ""
                            }`
                          : "Reverse charge"}
                    </span>
                  </div>
                </div>

                {!address?.line1 && (
                  <Banner tone='warn'>
                    <strong>No billing address on file.</strong> Sales tax cannot be calculated
                    without one, so this customer's invoices will go out untaxed. Enter the address
                    from their contract or PO below, or they will be asked for it when they add a
                    card.
                  </Banner>
                )}

                {owner.taxExemption.status === "exempt" && (
                  <Banner tone={owner.taxExemption.hasCertificateOnFile ? "info" : "warn"}>
                    <strong>Exempt from sales tax.</strong> They still pay for their seats as
                    normal; only the tax is zeroed.
                    {owner.taxExemption.expiresAt
                      ? ` Certificate expires ${moment(owner.taxExemption.expiresAt).format(
                          "MMM D, YYYY",
                        )}, after which tax resumes automatically.`
                      : " No expiry recorded."}
                    {!owner.taxExemption.hasCertificateOnFile && (
                      <>
                        {" "}
                        <strong>
                          No certificate document is stored - upload one, or this exemption cannot
                          be evidenced in an audit.
                        </strong>
                      </>
                    )}
                  </Banner>
                )}

                <div className='billing-actions'>
                  <PrimaryButton label='View bills' to={`/customer/bills/${owner.id}`} />
                  {owner.taxExemption.hasCertificateOnFile && (
                    <SecondaryButton
                      type='button'
                      label='Download tax certificate'
                      onClick={downloadCertificate}
                    />
                  )}
                  <SecondaryButton
                    type='button'
                    label={address?.line1 ? "Edit billing address" : "Add billing address"}
                    onClick={() => setShowAddress(true)}
                  />
                  <SecondaryButton
                    type='button'
                    label='Sales tax exemption'
                    onClick={() => setShowTaxExemption(true)}
                  />
                  <PrimaryButton
                    label={owner.is_direct ? "Remove billing exemption" : "Exempt from all billing"}
                    onClick={() =>
                      setPending({
                        action: "exemption",
                        type: "SOS",
                        currentIsDirect: owner.is_direct,
                        title: owner.is_direct
                          ? "Remove this customer's billing exemption?"
                          : "Exempt this customer from all billing?",
                        body: owner.is_direct ? (
                          <p>
                            Billing resumes for this customer from now on. This does not recreate
                            the subscription that was cancelled when they were exempted: they will
                            be set up again the next time a device is added, or you can do it here.
                          </p>
                        ) : (
                          <p>
                            This is a permanent, account-wide decision to never bill this customer
                            again, for SOS and Sensor both.{" "}
                            <strong>
                              Any live subscription is cancelled immediately with no refund.
                            </strong>{" "}
                            To waive a single charge instead, use a one-time comp on the bills page.
                          </p>
                        ),
                      })
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <div className='col-12'>
            {PRODUCT_TYPES.map((type) => (
              <Fragment key={type}>{renderSection(type)}</Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default CustomerBilling
